import React from "react";
import { Drawer, Stack, Box, TextField, Dropdown, Textarea, Inline, Toggle, Divider, Text, Button, Actions, Column, Columns } from "braid-design-system";
import Loading from "../Loading";
import { useToast } from "../../hooks/useToast";
import { useTranslation } from 'react-i18next';
import { ItReportTemplateResponse, uploadReport, getReportParamTypes, getReportTemplateCategories, uploadJrxmlWithUuid, getReportLanguages, ReportFilter, ItReportParamTypeResponse, ItReportCategoryResponse } from "../../services/reportService";
import { getAllLanguages, LanguageResponse } from "../../services/languageService";
import "./ReportDrawer.css";

interface ReportDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSaved?: (template: ItReportTemplateResponse) => void;
    initialData?: ItReportTemplateResponse | null;
}

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const ReportDrawer: React.FC<ReportDrawerProps> = ({ isOpen, onClose, onSaved, initialData }) => {
    const [reportName, setReportName] = React.useState<string>(initialData?.name || "");
    const [sqlQuery, setSqlQuery] = React.useState<string>(initialData?.sql_query || "");
    const [file, setFile] = React.useState<File | null>(null);
    const toast = useToast();
    const { t } = useTranslation();
    const [parameters, setParameters] = React.useState<ReportFilter[] | null>(initialData?.parameters || []);
    const [hasErrorName, setHasErrorName] = React.useState<boolean>(false);
    const [hasErrorQuery, setHasErrorQuery] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [reportParamTypes, setReportParamTypes] = React.useState<ItReportParamTypeResponse[]>([]);
    const [selectedParamTypes, setSelectedParamTypes] = React.useState<{ [key: string]: string } | null>(null);
    const [categoryId, setCategoryId] = React.useState<number | null>(initialData?.category_id || null);
    const [categories, setCategories] = React.useState<ItReportCategoryResponse[]>([]);
    const [languages, setLanguages] = React.useState<LanguageResponse[]>([]);
    const [uploadedLanguages, setUploadedLanguages] = React.useState<number[]>([]);
    const [languageFiles, setLanguageFiles] = React.useState<{ [languageId: number]: File | null }>({});
    const [uploadingLanguageId, setUploadingLanguageId] = React.useState<number | null>(null);

    React.useEffect(() => {
        if (isOpen && initialData) {
            setReportName(initialData.name || "");
            setSqlQuery(initialData.sql_query || "");
            setParameters(initialData.parameters || []);
            setCategoryId(initialData.category_id || null);

            // Load uploaded languages for this report
            if (initialData.id) {
                loadUploadedLanguages(initialData.id);
            }
        }
        if (!isOpen) {
            // reset when closed
            setReportName("");
            setSqlQuery("");
            setParameters([]);
            setFile(null);
            setCategoryId(null);
            setSelectedParamTypes(null);
            setHasErrorName(false);
            setHasErrorQuery(false);
        }
    }, [isOpen, initialData]);

    // Separate effect to map parameter types after they're loaded
    React.useEffect(() => {
        if (initialData?.parameters && reportParamTypes.length > 0) {
            const paramTypeMap: { [key: string]: string } = {};
            const updatedParams: ReportFilter[] = [];

            initialData.parameters.forEach((param: ReportFilter) => {
                let matchingType = reportParamTypes.find((pt) => pt.name === param.label);

                // If not found by name, try by type and label
                if (!matchingType) {
                    matchingType = reportParamTypes.find((pt) =>
                        pt.type === param.type && pt.label === param.label
                    );
                }

                // If still not found, try by type alone
                if (!matchingType) {
                    matchingType = reportParamTypes.find((pt) => pt.type === param.type);
                }

                if (matchingType?.id) {
                    paramTypeMap[param.name] = matchingType.id.toString();
                    // Update parameter with the matched type's data, but preserve the saved label if it exists
                    updatedParams.push({
                        ...param,
                        type: matchingType.type as ReportFilter['type'],
                        label: param.label || matchingType.label || '',  // Use saved label if exists, otherwise use default from param type
                        query: param.query || matchingType.sql_query || '',
                        is_required: param.is_required !== undefined ? param.is_required : (matchingType.is_required === 0 ? false : true),
                        value: param.value || matchingType.default_value || ''
                    });
                } else {
                    updatedParams.push(param);
                }
            });

            setSelectedParamTypes(paramTypeMap);
            setParameters(updatedParams);
        }
    }, [initialData?.parameters, reportParamTypes]);

    React.useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        Promise.all([getReportTemplateCategories(), getReportParamTypes()])
            .then(([cats, types]) => {
                setCategories(cats);
                setReportParamTypes(types);
            })
            .catch((error) => {
                toast.showError(t('page.report_management.drawer.error.fetch_meta', { defaultValue: 'Error fetching report meta: {{message}}', message: error?.message || 'Unknown error' }));
            })
            .finally(() => setLoading(false));
    }, [isOpen, toast]);

    React.useEffect(() => {
        if (sqlQuery) {
            const paramRegex = /:(\w+)/g;
            const paramsSet = new Set<string>();
            let match;
            while ((match = paramRegex.exec(sqlQuery)) !== null) {
                paramsSet.add(match[1]);
            }
            const paramsArray = Array.from(paramsSet).map((paramName) => ({
                name: paramName,
                type: 'string' as const,
                value: null,
                query: null,
                label: '',
                is_required: true,
            }));
            setParameters(paramsArray);
        } else {
            setParameters([]);
        }
    }, [sqlQuery]);

    const loadUploadedLanguages = async (reportId: number) => {
        try {
            const uploadedLangs = await getReportLanguages(reportId);
            setUploadedLanguages(uploadedLangs);
        } catch (error: any) {
            // Silently fail - not critical if we can't load  
            setUploadedLanguages([]);
        }
    };

    const loadAvailableLanguages = React.useCallback(async () => {
        try {
            const langData = await getAllLanguages();
            setLanguages(Array.isArray(langData.languages) ? langData.languages : []);
        } catch (error: any) {
            toast.showError(t('page.report_management.drawer.error.load_languages', { defaultValue: 'Failed to load languages: {{message}}', message: error?.message || 'Unknown error' }));
            setLanguages([]);
        }
    }, [toast]);

    const handleUploadLanguageFile = async (languageIndex: number) => {
        if (!initialData?.id || !initialData?.report_uuid) {
            toast.showError(t('page.report_management.drawer.error.must_save_before_upload', { defaultValue: 'Report must be saved first before uploading language files' }));
            return;
        }

        const file = languageFiles[languageIndex];
        if (!file) {
            toast.showError(t('page.report_management.drawer.error.select_file_first', { defaultValue: 'Please select a file first' }));
            return;
        }

        try {
            setUploadingLanguageId(languageIndex);

            // Get language code from language ID
            const language = languages.find(l => l.id === languageIndex);
            const languageCode = language?.code || 'en';

            // Use new UUID-based endpoint (POST /report/upload_jrxml/{report_uuid})
            await uploadJrxmlWithUuid(initialData.report_uuid, languageCode, file);

            // Refresh uploaded languages list
            const updatedLanguages = await getReportLanguages(initialData.id);
            setUploadedLanguages(updatedLanguages);

            setLanguageFiles({ ...languageFiles, [languageIndex]: null });
            toast.showSuccess(t('page.report_management.drawer.toast.language_uploaded', { defaultValue: 'Language file uploaded successfully' }));
        } catch (error: any) {
            toast.showError(t('page.report_management.drawer.error.upload_language', { defaultValue: 'Failed to upload language file: {{message}}', message: error?.message || 'Unknown error' }));
        } finally {
            setUploadingLanguageId(null);
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            loadAvailableLanguages();
        }
    }, [isOpen, loadAvailableLanguages]);

    const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
        e && e.preventDefault();
        if (!reportName) {
            setHasErrorName(true);
            return;
        }
        if (!sqlQuery) {
            setHasErrorQuery(true);
            return;
        }

        // Smart validation: 
        // - Create mode: Main file is REQUIRED
        // - Edit mode: Main file is OPTIONAL (already exists)
        const isEditMode = !!initialData?.id;
        if (!isEditMode && !file) {
            toast.showError(t('page.report_management.drawer.error.main_required', { defaultValue: 'Main report file is required for new reports' }));
            return;
        }

        try {
            setLoading(true);
            const response = await uploadReport(
                {
                    id: isEditMode ? initialData!.id : undefined,  // Pass ID for edit mode
                    name: reportName,
                    sql_query: sqlQuery,
                    jrxml_content_hash: '',
                    parameters: parameters || [],
                    category_id: categoryId || null,
                },
                file || undefined  // Optional for edit mode
            );
            toast.showSuccess(isEditMode ? t('page.report_management.drawer.success.updated', { defaultValue: 'Report updated successfully' }) : t('page.report_management.drawer.success.created', { defaultValue: 'Report created successfully' }));
            if (onSaved) onSaved(response);
            onClose();
        } catch (error: any) {
            toast.showError(t('page.report_management.drawer.error.save', { defaultValue: 'Error saving report: {{message}}', message: error?.message || 'Unknown error' }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer width="large" open={isOpen} onClose={onClose} title={initialData ? t('page.report_management.drawer.title.edit', { defaultValue: 'Edit Report' }) : t('page.report_management.drawer.title.add', { defaultValue: 'Add Report' })}>
            {loading && <Loading message={t('page.report_management.drawer.loading.please_wait', { defaultValue: 'Please wait...' })} />}
            <form className="report-drawer-form" onSubmit={handleSubmit} autoComplete="off">
                <Box padding="small">
                    <Stack space="medium">
                        <Columns space="large" collapseBelow="tablet">
                            <Column width="1/2">
                                <Stack space="large">
                                    <Stack space="medium">
                                        <TextField
                                            label={t('page.report_management.drawer.label.name', { defaultValue: 'Report name*' })}
                                            value={reportName}
                                            onChange={(e) => setReportName(e.currentTarget.value)}
                                            placeholder={t('page.report_management.drawer.placeholder.name', { defaultValue: 'Report Name' })}
                                            tone={hasErrorName ? "critical" : "neutral"}
                                            message={hasErrorName ? t('page.report_management.drawer.validation.name_required', { defaultValue: 'Report name is required' }) : undefined}
                                        />
                                        <Dropdown
                                            label={t('page.report_management.drawer.label.category', { defaultValue: 'Report Category' })}
                                            placeholder={t('page.report_management.drawer.placeholder.category', { defaultValue: 'Select report category' })}
                                            value={categoryId?.toString() || ''}
                                            onChange={e => {
                                                const selectedId = (e.target as HTMLSelectElement).value;
                                                setCategoryId(selectedId ? Number(selectedId) : null);
                                            }}
                                        >
                                            {categories.map((category) => (
                                                <option key={category.id} value={category.id?.toString()}>{category.name}</option>
                                            ))}
                                        </Dropdown>
                                        <Textarea
                                            label={t('page.report_management.drawer.label.sql', { defaultValue: 'SQL Query*' })}
                                            description={t('page.report_management.drawer.description.sql', { defaultValue: 'Add your SQL query here. Use :param_name for parameters.' })}
                                            value={sqlQuery}
                                            onChange={(e) => setSqlQuery(e.currentTarget.value)}
                                            placeholder={t('page.report_management.drawer.placeholder.sql', { defaultValue: 'SQL Query' })}
                                            lineLimit={10}
                                            tone={hasErrorQuery ? "critical" : "neutral"}
                                            message={hasErrorQuery ? t('page.report_management.drawer.validation.sql_required', { defaultValue: 'SQL query is required' }) : undefined}
                                        />
                                    </Stack>
                                </Stack>
                            </Column>
                            <Column width="1/2">
                                <Stack space="medium">
                                    <Text tone="info" weight="strong">{t('page.report_management.drawer.heading.filters', { defaultValue: 'Set Report Filters Configs' })}</Text>

                                    {parameters && parameters.length > 0 ? (
                                        parameters.map((param) => (
                                            <React.Fragment key={param.name}>
                                                <Inline space="small" alignY="center">
                                                    <Dropdown
                                                        label={`Parameter: ${param.name}`}
                                                        placeholder="Select parameter type"
                                                        value={selectedParamTypes?.[param.name]?.toString() || ''}
                                                        onChange={e => {
                                                            const selectedId = (e.target as HTMLSelectElement).value;
                                                            const selectedParamType = reportParamTypes.find(pt => pt.id?.toString() === selectedId);
                                                            const selectedParamTypeId = selectedParamType?.id;
                                                            if (selectedParamTypeId !== undefined && selectedParamTypeId !== null) {
                                                                setSelectedParamTypes((prevSelected) => ({
                                                                    ...(prevSelected || {}),
                                                                    [param.name]: selectedParamTypeId.toString()
                                                                }));

                                                                setParameters((prevParams) =>
                                                                    prevParams
                                                                        ? prevParams.map((p) =>
                                                                            p.name === param.name ? {
                                                                                ...p,
                                                                                type: selectedParamType?.type as ReportFilter['type'],
                                                                                label: selectedParamType?.label || '',
                                                                                query: selectedParamType?.sql_query || '',
                                                                                is_required: selectedParamType?.is_required === 0 ? false : true,
                                                                                value: selectedParamType?.default_value || ''
                                                                            } : p
                                                                        )
                                                                        : prevParams
                                                                );
                                                            }

                                                        }}
                                                    >
                                                        {reportParamTypes.map((paramType) => (
                                                            <option key={paramType.id} value={paramType.id}>{paramType.name}</option>
                                                        ))}

                                                    </Dropdown>
                                                    {param.type === 'select' && (
                                                        <TextField
                                                            label={`Options Query for ${param.name}`}
                                                            value={param.query || ''}
                                                            onChange={e => {
                                                                const value = e.currentTarget.value;
                                                                setParameters((prevParams) =>
                                                                    prevParams
                                                                        ? prevParams.map((p) =>
                                                                            p.name === param.name ? { ...p, query: value } : p
                                                                        )
                                                                        : prevParams
                                                                );
                                                            }}
                                                            placeholder="SELECT id, name FROM options_table"
                                                        />
                                                    )}
                                                    <TextField
                                                        label={`Label for ${param.name}`}
                                                        value={param.label || ''}
                                                        onChange={e => {
                                                            const value = e.currentTarget.value;
                                                            setParameters((prevParams) =>
                                                                prevParams
                                                                    ? prevParams.map((p) =>
                                                                        p.name === param.name ? { ...p, label: value } : p
                                                                    )
                                                                    : prevParams
                                                            );
                                                        }}
                                                        placeholder="Enter parameter label (e.g., 'From Date', 'Customer Name')"
                                                    />
                                                    {param.type && (
                                                        <Toggle label="is required" on={Boolean(param.is_required)} onChange={(checked) => {
                                                            setParameters((prevParams) =>
                                                                prevParams
                                                                    ? prevParams.map((p) =>
                                                                        p.name === param.name ? { ...p, is_required: checked } : p
                                                                    )
                                                                    : prevParams
                                                            );
                                                        }} />
                                                    )}
                                                </Inline>
                                                <Divider />
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <Text tone="secondary">{t('page.report_management.drawer.empty.no_parameters', { defaultValue: 'No parameters found in the SQL query.' })}</Text>
                                    )}
                                </Stack >
                            </Column>
                        </Columns>

                        <Divider />
                        <Stack space="medium">
                            <Text weight="strong">{t('page.report_management.drawer.heading.files', { defaultValue: 'Report Files' })}</Text>
                            <Text tone="secondary" size="small">{t('page.report_management.drawer.description.files', { defaultValue: 'Upload JRXML files for the report. Select a main version and optionally add language-specific files.' })}</Text>

                            <Stack space="medium">
                                {/* Main Report File Section */}
                                <Stack space="small">
                                    <Inline space="medium" alignY="center">
                                        <Text weight="strong">{t('page.report_management.drawer.label.main_file', { defaultValue: 'Main Report File' })}</Text>
                                        {file ? (
                                            <Text size="small" tone="positive">{t('page.report_management.drawer.status.selected', { defaultValue: '✓ Selected' })}</Text>
                                        ) : initialData?.id ? (
                                            <Text size="small" tone="secondary">{t('page.report_management.drawer.status.optional', { defaultValue: '(Optional - Update if needed)' })}</Text>
                                        ) : (
                                            <Text size="small" tone="critical">{t('page.report_management.drawer.status.required', { defaultValue: '* Required' })}</Text>
                                        )}
                                    </Inline>
                                    {initialData?.id && initialData.report_uuid ? (
                                        <Text tone="secondary" size="small">UUID: {String(initialData.report_uuid).substring(0, 12)}...</Text>
                                    ) : null}
                                    <Inline space="small" alignY="center">
                                        <input
                                            type="file"
                                            accept=".rptdesign,.jrxml"
                                            className="report-drawer-hidden-input"
                                            id="main-report-file"
                                            onChange={(e) => {
                                                if (e.target.files && e.target.files.length > 0) {
                                                    setFile(e.target.files[0]);
                                                } else {
                                                    setFile(null);
                                                }
                                            }}
                                        />
                                        <label htmlFor="main-report-file" className="report-drawer-file-label">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="small"
                                                onClick={() => document.getElementById('main-report-file')?.click()}
                                            >
                                                {file ? `✓ ${file.name}` : t('page.report_management.drawer.button.choose_file', { defaultValue: 'Choose File' })}
                                            </Button>
                                        </label>
                                        <Text tone="secondary" size="small">
                                            {file ? formatFileSize(file.size) : t('page.report_management.drawer.text.no_file_selected', { defaultValue: '(No file selected)' })}
                                        </Text>
                                    </Inline>
                                </Stack>

                                {/* Language Files Section - Only show if report is saved */}
                                {initialData?.id ? (
                                    <Stack space="small">
                                        <Text weight="strong" size="small">{t('page.report_management.drawer.heading.language_files', { defaultValue: 'Language-Specific Files' })}</Text>
                                        <Text tone="secondary" size="small">{t('page.report_management.drawer.description.language_files', { defaultValue: 'Upload translated versions. English version uses the main report file.' })}</Text>

                                        <Stack space="small">
                                            {languages.map((language) => {
                                                const isEnglish = language.id === 1;
                                                const isUploaded = uploadedLanguages.includes(language.id);

                                                // English language - uses main report file
                                                if (isEnglish) {
                                                    return (
                                                        <Box key={language.id} padding="small" borderRadius="small">
                                                            <Inline space="medium" alignY="center">
                                                                <Text size="small" weight="strong">{language.display_name}</Text>
                                                                <Text size="small" tone="positive">{t('page.report_management.drawer.language.uses_main', { defaultValue: '✓ Uses main report file' })}</Text>
                                                            </Inline>
                                                        </Box>
                                                    );
                                                }

                                                // Other languages - upload interface
                                                return (
                                                    <Stack key={language.id} space="small">
                                                        <Inline space="medium" alignY="center">
                                                            <Text weight="regular" size="small">{language.display_name}</Text>
                                                            {isUploaded ? <Text size="small" tone="positive">{t('page.report_management.drawer.language.uploaded', { defaultValue: '✓ Uploaded' })}</Text> : <Text size="small" tone="secondary">{t('page.report_management.drawer.language.not_uploaded', { defaultValue: 'Not uploaded' })}</Text>}
                                                        </Inline>
                                                        <Inline space="small" alignY="center">
                                                            <input
                                                                type="file"
                                                                accept=".rptdesign,.jrxml"
                                                                className="report-drawer-hidden-input"
                                                                id={`lang-file-${language.id}`}
                                                                onChange={(e) => {
                                                                    const file = e.currentTarget.files?.[0] || null;
                                                                    setLanguageFiles({ ...languageFiles, [language.id]: file });
                                                                }}
                                                            />
                                                            <label htmlFor={`lang-file-${language.id}`} className="report-drawer-file-label">
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="small"
                                                                    onClick={() => document.getElementById(`lang-file-${language.id}`)?.click()}
                                                                >
                                                                    {languageFiles[language.id] ? `✓ ${languageFiles[language.id]?.name}` : t('page.report_management.drawer.button.choose_file', { defaultValue: 'Choose File' })}
                                                                </Button>
                                                            </label>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="small"
                                                                tone="brandAccent"
                                                                onClick={() => handleUploadLanguageFile(language.id)}
                                                            >
                                                                {uploadingLanguageId === language.id ? t('page.report_management.drawer.button.uploading', { defaultValue: 'Uploading...' }) : t('page.report_management.drawer.button.upload', { defaultValue: 'Upload' })}
                                                            </Button>
                                                        </Inline>
                                                    </Stack>
                                                );
                                            })}
                                        </Stack>
                                    </Stack>
                                ) : null}
                            </Stack>
                        </Stack>

                        <Box display="flex" justifyContent={"flexEnd"} alignItems={'flexEnd'}>
                            <Actions>
                                <Button variant="ghost" type="submit" tone="brandAccent">{t('page.report_management.drawer.button.save', { defaultValue: 'Save' })}</Button>
                            </Actions>
                        </Box>
                    </Stack>
                </Box>
            </form>
        </Drawer>
    );
}

export default ReportDrawer;
